/**
 * Плейсхолдер: национальная группировка libphonenumber + международный код страны, цифры -> _
 */

import intlTelInput from 'intl-tel-input';

export type PhoneCountryData = {
    dialCode?: string;
    iso2?: string;
} | null;

type IntlUtils = {
    getExampleNumber: (iso2: string, national: boolean, numberType: number) => string;
    formatNumber: (number: string, iso2: string, format: number) => string;
    getCoreNumber: (number: string, iso2: string) => string;
    numberType: { MOBILE: number };
    numberFormat: { NATIONAL: number };
};

function getUtils(): IntlUtils | null {
    const g = intlTelInput as typeof intlTelInput & { utils?: IntlUtils | null };
    return g.utils ?? null;
}

function dialPrefix(country: PhoneCountryData): string {
    const dialDigits = (country?.dialCode ?? '').replace(/\D/g, '');
    return dialDigits ? `+${dialDigits}` : '+';
}

function digitsToUnderscores(formatted: string): string {
    return formatted.replace(/\d/g, '_');
}

function findNationalSignificantStart(nationalFormatted: string, coreDigits: string): number {
    if (!coreDigits) {
        return 0;
    }

    const idx = nationalFormatted.indexOf(coreDigits.charAt(0));
    if (idx < 0) {
        return 0;
    }

    let start = idx;
    if (start > 0 && nationalFormatted[start - 1] === '(') {
        start -= 1;
    }

    const digitsFromStart = nationalFormatted.slice(start).replace(/\D/g, '');
    if (digitsFromStart.startsWith(coreDigits)) {
        return start;
    }

    return idx;
}

function maskFromUtils(exampleNumber: string, country: PhoneCountryData): string | null {
    const utils = getUtils();
    const iso2 = country?.iso2?.toLowerCase();
    const dialDigits = (country?.dialCode ?? '').replace(/\D/g, '');

    if (!utils || !iso2 || !dialDigits) {
        return null;
    }

    let international = exampleNumber?.trim() ?? '';
    if (!international) {
        international = utils.getExampleNumber(iso2, false, utils.numberType.MOBILE);
    }
    if (!international) {
        return null;
    }

    const nationalFormatted = utils.formatNumber(
        international,
        iso2,
        utils.numberFormat.NATIONAL
    );
    const coreDigits = utils.getCoreNumber(international, iso2);
    if (!nationalFormatted || !coreDigits) {
        return null;
    }

    const start = findNationalSignificantStart(nationalFormatted, coreDigits);
    const nationalPart = digitsToUnderscores(nationalFormatted.slice(start));

    return `${dialPrefix(country)} ${nationalPart}`;
}

function maskInternationalDigits(formatted: string, dialDigits: string): string {
    if (!dialDigits || !formatted.startsWith('+')) {
        return digitsToUnderscores(formatted);
    }

    const prefix = `+${dialDigits}`;
    if (!formatted.startsWith(prefix)) {
        return digitsToUnderscores(formatted);
    }

    return prefix + digitsToUnderscores(formatted.slice(prefix.length));
}

/**
 * @param exampleNumber - пример от intl-tel-input (international, MOBILE)
 * @param country - выбранная страна (dialCode без +)
 */
export function buildPhonePlaceholder(exampleNumber: string, country: PhoneCountryData): string {
    try {
        const fromUtils = maskFromUtils(exampleNumber, country);
        if (fromUtils) {
            return fromUtils;
        }

        const formatted = exampleNumber?.trim();
        if (formatted) {
            const dialDigits = (country?.dialCode ?? '').replace(/\D/g, '');
            return maskInternationalDigits(formatted, dialDigits);
        }
    } catch (err) {
        console.warn('[PhoneMask] buildPhonePlaceholder failed', err);
    }

    return '+ (___) ___-__-__';
}
